import { clamp, damp, deltaDeg, wrapDeg360 } from '../util/units.js'

/**
 * The pilot's head, which is not bolted to the airframe.
 *
 * WHY THIS EXISTS
 * Every cockpit view in every simulator starts life welded rigidly to the
 * aircraft's attitude — camera quaternion copied from airframe quaternion, one
 * line, obviously correct. And it is the single biggest reason cockpit footage
 * from a simulator looks nothing like cockpit footage from an aeroplane.
 *
 * A real head is on a neck, on a torso, in a harness, and all of that has
 * compliance. Roll the aircraft and the head arrives a fraction of a second
 * later. More than that, the pilot is looking AT something — the ball, the
 * deck, the horizon — and the vestibulo-ocular reflex is actively working to
 * keep the gaze there while the airframe moves underneath it. Nobody's head
 * describes a perfect arc around the roll axis.
 *
 * The numbers here are small on purpose. Seventy milliseconds of lag and a few
 * per cent of roll relief is not something you can point at in a screenshot; it
 * is entirely a motion cue, and it is the difference between a camera bolted to
 * a jet and a person sitting in one.
 *
 * Kept as plain angle arithmetic rather than quaternion interpolation so it can
 * be tested without a graphics library, and so each axis can have its own lag —
 * which it should, because your neck resists roll far more than it resists
 * pitch.
 */

/** How fast the head catches up in each axis, per second. Higher is stiffer. */
export const HEAD_RATE = {
  heading: 15,
  pitch: 13,
  roll: 9,
}

/**
 * How much of the aircraft's roll the head actually takes up.
 *
 * Not 1. Under bank the pilot's head tilts a little less than the airframe,
 * because they are looking at a horizon that is not tilting. Not far off 1
 * either — this is a fighter with a harness, not a passenger craning at a
 * window.
 */
export const ROLL_FOLLOW = 0.94

/**
 * Advance the head attitude one step toward the airframe's.
 *
 * @param {{headingDeg:number,pitchDeg:number,rollDeg:number}} head mutated
 * @param {{headingDeg:number,pitchDeg:number,rollDeg:number}} craft
 * @param {number} dt seconds
 * @returns the same head object
 */
export function stepHead(head, craft, dt) {
  if (!(dt > 0)) return head

  // Heading has to go the short way round. Damping 359 toward 1 the naive way
  // sends the pilot's head 358 degrees the wrong way, once per pattern, at the
  // exact moment the aeroplane rolls out of the break.
  const err = deltaDeg(craft.headingDeg, head.headingDeg)
  head.headingDeg = wrapDeg360(head.headingDeg + err * follow(HEAD_RATE.heading, dt))

  head.pitchDeg = damp(head.pitchDeg, craft.pitchDeg, HEAD_RATE.pitch, dt)
  head.rollDeg = damp(head.rollDeg, craft.rollDeg * ROLL_FOLLOW, HEAD_RATE.roll, dt)
  return head
}

/** Snap the head to the airframe, for a reset or a view change. */
export function setHead(head, craft) {
  head.headingDeg = craft.headingDeg
  head.pitchDeg = craft.pitchDeg
  head.rollDeg = craft.rollDeg * ROLL_FOLLOW
  return head
}

/** Fraction of the remaining error closed in dt at a given rate. */
function follow(rate, dt) {
  return clamp(1 - Math.exp(-rate * dt), 0, 1)
}

/**
 * Sideways head displacement from rolling, metres.
 *
 * Your head is about half a metre above the seat pivot, so rolling the aircraft
 * swings it sideways before your body catches up. A couple of centimetres, and
 * it is the cue that sells the roll as something happening to a person rather
 * than to a viewport.
 */
export function headSwayM(rollRateDegS) {
  return clamp(-rollRateDegS * 0.0009, -0.035, 0.035)
}
